// Centralized API Configuration for E8 Tracker
// Uses Supabase Edge Functions for backend API

// Get Supabase project reference from the URL
const getSupabaseProjectRef = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
  if (supabaseUrl) {
    // Extract project reference from URL like https://vracotqahobdmgywcqow.supabase.co
    const match = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)
    return match ? match[1] : null
  }
  return null
}

// Build Supabase Functions URL from environment variables
const getSupabaseFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
  if (supabaseUrl) {
    // Replace /rest/v1 or just add /functions/v1 to base URL
    return supabaseUrl.replace(/\/$/, '') + '/functions/v1'
  }
  return null
}

// API Configuration
const API_CONFIG = {
  // Supabase Edge Functions
  SUPABASE_PROJECT_REF: getSupabaseProjectRef(),
  SUPABASE_BASE_URL: getSupabaseFunctionsUrl(),
  
  // Admin API Endpoints
  ENDPOINTS: {
    // Admin user management
    ADMIN_LIST_USERS: '/admin-list-users',
    ADMIN_APPROVE_USER: '/admin-approve-user', 
    ADMIN_DELETE_USER: '/admin-delete-user',
    
    // Module permission management
    ADMIN_MODULE_PERMISSIONS: '/admin-module-permissions',
    ADMIN_PERMISSION_TEMPLATES: '/admin-permission-templates',
    USER_MODULE_ACCESS: '/user-module-access',
    ORGANISATION_AUTHORISATION: '/organisation-authorisation'
  }
}

// Get the API base URL
export const getApiBaseUrl = () => {
  if (!API_CONFIG.SUPABASE_BASE_URL) {
    console.error('⚠️ API Config: Supabase base URL not configured')
    throw new Error('API configuration error: Supabase base URL required')
  }
  return API_CONFIG.SUPABASE_BASE_URL
}

// Get full endpoint URL
export const getApiEndpoint = (endpointKey) => {
  const baseUrl = getApiBaseUrl()
  const endpoint = API_CONFIG.ENDPOINTS[endpointKey]
  
  if (!endpoint) {
    throw new Error(`Unknown API endpoint: ${endpointKey}`)
  }
  
  return `${baseUrl}${endpoint}`
}

// Admin API helper functions
export const adminAPI = {
  /**
   * List all users (admin only)
   */
  async listUsers(accessToken) {
    const url = getApiEndpoint('ADMIN_LIST_USERS')
    console.log(`🔗 AdminAPI: Fetching users from ${url}`)
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
    }
    
    return await response.json()
  },
  
  /**
   * Approve a user (admin only)
   */
  async approveUser(userId, accessToken) {
    const url = getApiEndpoint('ADMIN_APPROVE_USER')
    console.log(`🔗 AdminAPI: Approving user ${userId} at ${url}`)
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ user_id: userId })
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
    }
    
    return await response.json()
  },
  
  /**
   * Delete a user (admin only)
   */
  async deleteUser(userId, accessToken) {
    const url = getApiEndpoint('ADMIN_DELETE_USER')
    console.log(`🔗 AdminAPI: Deleting user ${userId} at ${url}`)
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ user_id: userId })
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
    }
    
    return await response.json()
  }
}

// Configuration debugging
export const getApiConfig = () => ({
  supabaseProjectRef: API_CONFIG.SUPABASE_PROJECT_REF,
  supabaseBaseUrl: API_CONFIG.SUPABASE_BASE_URL,
  currentBaseUrl: getApiBaseUrl(),
  endpoints: API_CONFIG.ENDPOINTS
})

// Log current configuration on import
console.log('🔗 API Configuration loaded:', getApiConfig())

export default API_CONFIG
